import React, { useState, useEffect } from "react";
import { X, Mail, Wallet, CheckCircle, Copy } from "lucide-react";
import { toast } from "react-toastify";
import { WalletConnectModal } from "./WalletConnectModal";

export const WalletSubmitModal = ({ isOpen, onClose, connectedWallet, onConnectWallet, onModalStateChange }) => {
  const [email, setEmail] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [existingWallet, setExistingWallet] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);

  useEffect(() => {
    if (isOpen && connectedWallet) {
      setWalletAddress(connectedWallet);
      checkExistingWallet(connectedWallet);
    }
  }, [isOpen, connectedWallet]);

  // Notify parent component about modal state changes
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isOpen || showConnectModal);
    }
  }, [isOpen, showConnectModal, onModalStateChange]);

  const checkExistingWallet = async (address) => {
    try {
      const response = await fetch("https://api.codedestate.com/user/checkWallet", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ walletAddress: address }),
      });
      
      const data = await response.json();
      if (data.exists) {
        setExistingWallet(data);
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error("Error checking wallet:", error);
    }
  };

  const onCloseModal = () => {
    onClose();
    setEmail("");
    setWalletAddress("");
    setIsSubmitted(false);
    setExistingWallet(null);
  };

  const handleSubmit = async () => {
    if (!walletAddress.trim()) {
      toast.error("Please enter a wallet address");
      return;
    }

    // Basic email validation (only if email is provided)
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    setLoading(true);

    const attemptSubmit = async (attemptNumber = 1) => {
      try {
        const response = await fetch("https://api.codedestate.com/user/submitWallet", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            walletAddress: walletAddress.trim(),
            email: email.trim(),
          }),
        });

        const data = await response.json();

        if (data.success) {
          setIsSubmitted(true);
          setExistingWallet({
            walletAddress: walletAddress.trim(),
            email: email.trim(),
            submittedAt: new Date().toISOString(),
          });

          toast.success("Wallet submitted successfully! 🎉");
          return true;
        } else {
          if (attemptNumber === 1) {
            console.log("First attempt failed, retrying...");
            await new Promise(resolve => setTimeout(resolve, 1000));
            return await attemptSubmit(2);
          } else {
            toast.error(data.message || "Failed to submit wallet");
            return false;
          }
        }
      } catch (error) {
        console.error(`Submit error (attempt ${attemptNumber}):`, error);
        
        if (attemptNumber === 1 && (error.status === 500 || !error.status)) {
          console.log("First attempt failed with 500/network error, retrying...");
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await attemptSubmit(2);
        } else {
          toast.error(attemptNumber === 1 ? "Failed to submit wallet. Please try again." : "Failed to submit wallet after retry. Please try again later.");
          return false;
        }
      }
    };

    try {
      await attemptSubmit(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    toast.success("Wallet address copied to clipboard!");
  };

  const handleWalletConnected = (address) => {
    setWalletAddress(address);
    if (onConnectWallet) {
      onConnectWallet(address);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onCloseModal}
    >
      <div 
        className="w-[95vw] sm:w-[90vw] md:w-[480px] max-w-[480px] min-w-[280px] rounded-[12px] sm:rounded-[16px] bg-white shadow-xl border-0 p-0 z-50 max-h-[90vh] overflow-y-auto mb-4 sm:mb-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <button
            onClick={onCloseModal}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
          </button>
          
          <div className="text-center pt-6 sm:pt-8 pb-4 sm:pb-6 px-4 sm:px-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {isSubmitted ? "Wallet Activated!" : "Submit Your Wallet"}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm px-2">
              {isSubmitted 
                ? "Your wallet is now connected to Coded Estate. You're all set!"
                : "Connect your wallet to submit verification for Coded Estate perps testing"
              }
            </p>
          </div>

          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            {isSubmitted ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center p-4 bg-green-50 rounded-xl border border-green-200">
                  <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold text-green-800">Wallet Activated</div>
                    <div className="text-sm text-green-600">
                      {existingWallet?.email || email}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center mb-2">
                    <Wallet className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Wallet Address</span>
                  </div>
                  <div className="text-sm text-gray-600 font-mono break-all">
                    {existingWallet?.walletAddress || walletAddress}
                  </div>
                </div>

                <div className="w-full">
                  <button
                    onClick={onCloseModal}
                    className="w-full py-3 px-4 text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
                  >
                    Continue to Coded Estate
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Wallet className="w-4 h-4 inline mr-1" />
                    Wallet Address
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      placeholder="Enter your wallet address"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 font-mono text-sm"
                    />
                    {connectedWallet && (
                      <button
                        onClick={handleCopyAddress}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Copy connected wallet address"
                      >
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <button
                      onClick={handleSubmit}
                      disabled={loading || !walletAddress.trim()}
                      className="w-full py-3 px-4 text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? "Submitting..." : "Submit Wallet"}
                    </button>
                  </div>
                  
                  {!connectedWallet && (
                    <div className="flex-1">
                      <button
                        onClick={() => setShowConnectModal(true)}
                        className="w-full py-3 px-4 text-base font-semibold bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-all duration-200"
                      >
                        Connect Wallet
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to our terms of service and privacy policy.
                </p>
              </div>
            )}
          </div>
                 </div>
       </div>
       
       {/* Wallet Connect Modal */}
       <WalletConnectModal 
         isOpen={showConnectModal}
         onClose={() => setShowConnectModal(false)}
         onWalletConnected={handleWalletConnected}
       />
     </div>
   );
 };
